import type { KnowledgeFolderNode } from '@sbc/domain-models/knowledge-base';
import { ChevronRight, FolderPlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api/client';
import { useCreateFolder, useDeleteFolder, useUpdateFolder } from '@/lib/api/knowledge-base';
import { cn } from '@/lib/utils';

type Action =
  | { kind: 'add' | 'rename' | 'move' | 'delete'; folderId: string }
  | { kind: 'add-root' };

// Radix Select has no empty-string item value; '' remains the "move to root" draft.
const ROOT_TARGET = '__root__';

function depthOf(path: string): number {
  return path.split('/').length - 1;
}

// Folder ids that are `id` or live beneath it — invalid move targets (would cycle).
function selfAndDescendants(folders: KnowledgeFolderNode[], id: string): Set<string> {
  const result = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const folder of folders) {
      if (folder.parentId !== null && result.has(folder.parentId) && !result.has(folder.id)) {
        result.add(folder.id);
        grew = true;
      }
    }
  }
  return result;
}

export function KbTree({
  folders,
  articleCountByFolder,
  selectedId,
  onSelect,
}: {
  folders: KnowledgeFolderNode[];
  articleCountByFolder: Map<string, number>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();

  const [action, setAction] = useState<Action | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sorted = [...folders].toSorted((a, b) => a.path.localeCompare(b.path));

  const fail = (caught: unknown, fallback: string) => {
    setError(caught instanceof ApiError ? caught.message : fallback);
  };
  const reset = () => {
    setAction(null);
    setDraft('');
    setError(null);
  };

  const open = (next: Action, initial: string) => {
    setError(null);
    setDraft(initial);
    setAction(next);
  };

  const submitAdd = (parentId: string | null) => {
    createFolder.mutate(
      { parentId, slug: draft.trim() },
      { onSuccess: reset, onError: (caught) => fail(caught, 'Could not create the folder.') },
    );
  };

  const submitRename = (folderId: string) => {
    updateFolder.mutate(
      { id: folderId, input: { slug: draft.trim() } },
      { onSuccess: reset, onError: (caught) => fail(caught, 'Could not rename the folder.') },
    );
  };

  const submitMove = (folderId: string) => {
    updateFolder.mutate(
      { id: folderId, input: { parentId: draft === '' ? null : draft } },
      { onSuccess: reset, onError: (caught) => fail(caught, 'Could not move the folder.') },
    );
  };

  const submitDelete = (folderId: string) => {
    deleteFolder.mutate(folderId, {
      onSuccess: () => {
        if (selectedId === folderId) {
          onSelect('');
        }
        reset();
      },
      onError: (caught) => fail(caught, 'Could not delete the folder.'),
    });
  };

  const busy = createFolder.isPending || updateFolder.isPending || deleteFolder.isPending;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Folders</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => open({ kind: 'add-root' }, '')}
        >
          <FolderPlus className="size-4" />
          New
        </Button>
      </div>

      {action?.kind === 'add-root' && (
        <SlugForm
          value={draft}
          onChange={setDraft}
          placeholder="new-folder-slug"
          disabled={busy}
          onSubmit={() => submitAdd(null)}
          onCancel={reset}
        />
      )}

      <ul className="space-y-0.5">
        {sorted.map((folder) => {
          const isSelected = folder.id === selectedId;
          const count = articleCountByFolder.get(folder.id) ?? 0;
          const moveTargets = sorted.filter(
            (candidate) => !selfAndDescendants(folders, folder.id).has(candidate.id),
          );
          return (
            <li key={folder.id}>
              <div
                className={cn(
                  'group flex items-center gap-1 rounded-md pr-1 hover:bg-[var(--color-accent)]',
                  isSelected && 'bg-[var(--color-accent)]',
                )}
                style={{ paddingLeft: `${depthOf(folder.path) * 16 + 4}px` }}
              >
                <button
                  type="button"
                  className="flex flex-1 items-center gap-1 py-1.5 text-left text-sm"
                  onClick={() => onSelect(folder.id)}
                >
                  <ChevronRight className="size-3.5 text-[var(--color-muted-foreground)]" />
                  <span className="font-medium">{folder.slug}</span>
                  <span className="text-xs text-[var(--color-muted-foreground)]">({count})</span>
                </button>
                <div className="flex items-center opacity-0 group-hover:opacity-100">
                  <IconButton
                    label="Add subfolder"
                    onClick={() => open({ kind: 'add', folderId: folder.id }, '')}
                  >
                    <Plus className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label="Rename"
                    onClick={() => open({ kind: 'rename', folderId: folder.id }, folder.slug)}
                  >
                    <Pencil className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label="Move"
                    onClick={() =>
                      open({ kind: 'move', folderId: folder.id }, folder.parentId ?? '')
                    }
                  >
                    <ChevronRight className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label="Delete"
                    onClick={() => open({ kind: 'delete', folderId: folder.id }, '')}
                  >
                    <Trash2 className="size-3.5" />
                  </IconButton>
                </div>
              </div>

              {action?.kind === 'add' && action.folderId === folder.id && (
                <SlugForm
                  value={draft}
                  onChange={setDraft}
                  placeholder="subfolder-slug"
                  disabled={busy}
                  onSubmit={() => submitAdd(folder.id)}
                  onCancel={reset}
                />
              )}
              {action?.kind === 'rename' && action.folderId === folder.id && (
                <SlugForm
                  value={draft}
                  onChange={setDraft}
                  placeholder="new-slug"
                  disabled={busy}
                  onSubmit={() => submitRename(folder.id)}
                  onCancel={reset}
                />
              )}
              {action?.kind === 'move' && action.folderId === folder.id && (
                <div className="flex items-center gap-2 py-1 pl-6">
                  <Select
                    value={draft || ROOT_TARGET}
                    disabled={busy}
                    onValueChange={(value) => setDraft(value === ROOT_TARGET ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ROOT_TARGET}>— Root —</SelectItem>
                      {moveTargets.map((target) => (
                        <SelectItem key={target.id} value={target.id}>
                          {target.path}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => submitMove(folder.id)}
                  >
                    Move
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={reset}>
                    Cancel
                  </Button>
                </div>
              )}
              {action?.kind === 'delete' && action.folderId === folder.id && (
                <div className="flex items-center gap-2 py-1 pl-6 text-sm">
                  <span>Delete “{folder.slug}”?</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    className="text-[var(--color-destructive)]"
                    onClick={() => submitDelete(folder.id)}
                  >
                    Confirm
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={reset}>
                    Cancel
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {sorted.length === 0 && !action && (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          No folders yet. Create one to start.
        </p>
      )}
      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
    </div>
  );
}

function SlugForm({
  value,
  onChange,
  placeholder,
  disabled,
  onSubmit,
  onCancel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-2 py-1 pl-6">
      <Input
        value={value}
        placeholder={placeholder}
        className="h-8"
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onSubmit();
          }
          if (event.key === 'Escape') {
            onCancel();
          }
        }}
      />
      <Button type="button" size="sm" disabled={disabled || value.trim() === ''} onClick={onSubmit}>
        Save
      </Button>
      <Button type="button" size="sm" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="px-1.5"
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
